import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { FieldPath, useFormContext, useFormState } from 'react-hook-form';
import { CurrencyInputField, SelectField, ToggleField } from '@brighte/form-components';
import {
  EmploymentType,
  IncomeVerificationType,
  LABELS,
  PENSIONER_CODE_NOT_REQUIRED_INCOME_VERIFICATION,
  PENSIONER_CODE_REQUIRED_INCOME_VERIFICATION,
  Period,
  isPensionerIncomeValidationRequired,
} from '@brighte/validation';
import FinancialAlertBanner from '@components/FinancialAlertBanner/FinancialAlertBanner';
import { css } from '@emotion/css';
import { useLaunchDarklyContextUpdate } from '@hooks';
import { useApplicationData } from '@provider';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Columns } from '@spark-web/columns';
import { Field } from '@spark-web/field';
import { InformationCircleIcon } from '@spark-web/icon';
import { useDisclosure } from '@spark-web/utils';
import {
  FinancialFieldTypes,
  INCOME_VERIFICATION,
  PLACEHOLDER,
  TIME_FREQUENCY,
  convertAmountToMonthlyPeriod,
  getAdminUserId,
  isFieldExists,
  productIds,
} from '@utils';
import IncomeVerificationOptions from './IncomeVerificationOptions';
import { getFieldLabels } from './helpers';
import { useIncomeVerificationConditions } from './hooks';

type UploadModalDisclosure = { isOpen: boolean; onOpen: () => void; onToggle: () => void };

type IncomeFieldsProps = {
  incomeType: 'primary' | 'additional';
  financialProductId: string;
  formFields: string[];
  occupationIncomeThreshold?: number;
  suppressVerificationCta?: boolean;
  showAdditionalIncomeToggle?: boolean;
  uploadModalDisclosure?: UploadModalDisclosure;
};

function IncomeFields({
  incomeType,
  financialProductId,
  formFields,
  occupationIncomeThreshold,
  suppressVerificationCta = false,
  showAdditionalIncomeToggle = false,
  uploadModalDisclosure,
}: IncomeFieldsProps) {
  const internalDisclosure = useDisclosure();
  const { isOpen, onOpen, onToggle } = uploadModalDisclosure ?? internalDisclosure;
  const [showBanner, setShowBanner] = useState(false);
  const [isLockedInSession, setIsLockedInSession] = useState(false);
  const [isFirstFailureAllowEdit, setIsFirstFailureAllowEdit] = useState(false);
  const { control, setValue, register, trigger, watch } = useFormContext<IncomeVerificationType>();
  const {
    quote,
    Identification: identification,
    Finance: finance,
  } = useApplicationData(['quote', 'Identification', 'Finance']);
  const { totalPurchaseAmount = 0, depositAmount = 0 } = quote ?? {};
  const financeAmount = totalPurchaseAmount - depositAmount;
  const isBrightePay = financialProductId === productIds.BRIGHTE_PAY;
  const isCashApplication = finance?.payment?.isCashApplication ?? false;

  const dateOfBirth = new Date(identification?.applicant.dateOfBirth);
  const flags = useFlags();
  const updateLDContext = useLaunchDarklyContextUpdate();

  useEffect(() => {
    if (financialProductId) {
      updateLDContext({ financialProduct: { key: financialProductId } });
    }
  }, [financialProductId]);

  const isIncomeVerificationFeedbackEnabled = flags['income-verification-feedback'] ?? false;

  const { ADDITIONAL_INCOME } = LABELS;
  const { MANUAL_VERIFICATION_SUBMITTED } = INCOME_VERIFICATION;

  const watchFields = [
    `applicant.income.${incomeType}.documents`,
    `applicant.income.${incomeType}.bankStatementComplete`,
    'applicant.income.isAdditionalIncome',
    'applicant.income.isSkipPrimaryIncome',
    'applicant.income.isSkipAdditionalIncome',
    'incomeVerification',
    'applicant.employment.type',
    `applicant.income.${incomeType}.amount`,
    `applicant.income.${incomeType}.period`,
    'applicant.employment.pensioner.pensionType',
    `applicant.income.primary.amount`,
    `applicant.income.primary.period`,
  ] as const;

  const [
    documents,
    isBankStatementComplete,
    isAdditionalIncome,
    isPrimaryVerificationSkipped,
    isAdditionalIncomeSkipped,
    isManualVerificationRequested,
    employmentType,
    applicantIncomeAmount,
    applicantIncomePeriod,
    applicantPensionerType,
    applicantPrimaryIncomeAmount,
    applicantPrimaryIncomePeriod,
  ] = watch(watchFields);

  const { errors } = useFormState({ control });

  const label = getFieldLabels(incomeType);
  const documentUploaded = documents ?? [];

  const handleVerificationFailed = (wasAlreadyFailed: boolean) => {
    if (wasAlreadyFailed) {
      setIsLockedInSession(true);
      setValue('incomeVerification', 'Failed');
    } else {
      setIsFirstFailureAllowEdit(true);
    }
  };

  const isManualVerificationLocked =
    incomeType === 'primary' && isIncomeVerificationFeedbackEnabled && isLockedInSession;
  // Manual review after a failed verification still requires uploaded documents. "Verified" means
  // actual proof is present (documents or a completed bank statement) — the manual-review flags on
  // their own no longer count. So if the user deletes their documents after a failure, the step
  // reverts to asking for documents instead of showing the "submitted for manual review" state.
  const hasIncomeProof = documentUploaded.length > 0 || isBankStatementComplete;
  const isIncomeVerified = hasIncomeProof;
  const modifiedFieldGapClass = css({
    '& > div > div:first-child': {
      marginTop: '0',
    },
  });

  const isPensioner = employmentType === EmploymentType.PENSIONER;
  const pensionerIncomeValidationCondition =
    isPensioner && applicantPensionerType
      ? isPensionerIncomeValidationRequired(
          applicantPensionerType,
          dateOfBirth,
          convertAmountToMonthlyPeriod(
            applicantPrimaryIncomeAmount ?? 0,
            applicantPrimaryIncomePeriod ?? Period.Monthly,
          ),
        )
      : false;

  // check if documents/bank statement is required from schema for primary applicant
  // we can change this should we need to set the documents/bank statement to not required for additional income
  const isDocumentsRequired =
    incomeType === 'primary'
      ? isFieldExists(formFields, `applicant.income.${incomeType}.documents`) ||
        isFieldExists(formFields, `applicant.income.${incomeType}.bankStatementToken`)
      : Boolean(isAdditionalIncome);

  const isDocumentSkipped = incomeType === 'primary' ? isPrimaryVerificationSkipped : isAdditionalIncomeSkipped;

  useEffect(() => {
    if (isManualVerificationRequested === 'Failed' && !isLockedInSession && !isFirstFailureAllowEdit && !isOpen) {
      setIsLockedInSession(true);
    }
  }, [isFirstFailureAllowEdit, isLockedInSession, isManualVerificationRequested, isOpen]);

  const handleSkipVerification = () => {
    const skipIncomeType = incomeType === 'primary' ? 'isSkipPrimaryIncome' : 'isSkipAdditionalIncome';
    setValue(`applicant.income.${skipIncomeType}`, true);
  };

  const { isIncomeVerificationRequired, isAdditionalIncomeVerificationRequired } = useIncomeVerificationConditions({
    financialProductId,
    applicantPrimaryIncomeAmount: applicantPrimaryIncomeAmount ?? 0,
    applicantPrimaryIncomePeriod: applicantPrimaryIncomePeriod ?? Period.Monthly,
    occupationIncomeThreshold: occupationIncomeThreshold ?? 0,
    isDocumentsRequired,
    financeAmount,
    employmentType,
    pensionerType: applicantPensionerType!,
    dateOfBirth,
    isCashApplication,
  });

  useEffect(() => {
    // Set Pensioner code based on pensioner income validation condition
    if (isPensioner && isFieldExists(formFields, 'applicant.employment.pensioner.code')) {
      setValue(
        'applicant.employment.pensioner.code' as FieldPath<IncomeVerificationType>,
        pensionerIncomeValidationCondition
          ? PENSIONER_CODE_REQUIRED_INCOME_VERIFICATION
          : PENSIONER_CODE_NOT_REQUIRED_INCOME_VERIFICATION,
      );
    }

    const skipVerification = incomeType === 'additional' && !isAdditionalIncomeVerificationRequired();
    setValue(`applicant.income.isSkipAdditionalIncome`, skipVerification);
  }, [employmentType, incomeType, pensionerIncomeValidationCondition]);

  const isAdmin = !!getAdminUserId();

  const renderAdditionalIncomeToggle = () => {
    return (
      <Box marginTop="xsmall">
        <ToggleField
          name="applicant.income.isAdditionalIncome"
          label={ADDITIONAL_INCOME}
          description={INCOME_VERIFICATION.ADDITIONAL_INCOME_OPTIONS_DESCRIPTION}
          control={control}
          layout="inline"
          setValue={setValue}
          onChange={e => {
            if (e) setValue('applicant.income.additional.documents', []);
          }}
        />
      </Box>
    );
  };

  const renderVerificationDetail = () => {
    if (suppressVerificationCta) {
      if (showAdditionalIncomeToggle && incomeType === 'primary') {
        return renderAdditionalIncomeToggle();
      }
      return null;
    }

    //Do not render document requirements and warning for BNPL if income and period is still empty
    if (!applicantIncomeAmount && !applicantIncomePeriod && isBrightePay) {
      return null;
    }

    // If income verification is not required, do not render any verification details, only render additional income toggle
    if (incomeType === 'primary' && !isIncomeVerificationRequired()) {
      return renderAdditionalIncomeToggle();
    }

    // If additional income verification is not required, do not render any verification details
    if (incomeType === 'additional' && !isAdditionalIncomeVerificationRequired()) {
      return null;
    }

    if (isIncomeVerificationRequired()) {
      let verificationAlertMessage = INCOME_VERIFICATION.DOCUMENTS_PROVIDED;

      if (isBankStatementComplete) {
        verificationAlertMessage = INCOME_VERIFICATION.BANK_STATEMENT_PROVIDED;
      }

      if (
        incomeType === 'primary' &&
        isIncomeVerificationFeedbackEnabled &&
        (isManualVerificationLocked || isFirstFailureAllowEdit)
      ) {
        verificationAlertMessage = MANUAL_VERIFICATION_SUBMITTED;
      }

      return (
        <>
          <Field label={INCOME_VERIFICATION.UPLOAD_SUPPORTING_DOCUMENTS} description={label.incomeDocumentDescription}>
            {isAdmin ? (
              <Columns gap="xxlarge" collapseBelow="tablet">
                <Button tone="neutral" onClick={handleSkipVerification}>
                  Skip verification for now
                </Button>
                <Button prominence="low" onClick={onOpen}>
                  {isIncomeVerified ? `Edit documents` : `Upload documents`}
                </Button>
              </Columns>
            ) : (
              <Button
                prominence="low"
                tone={!!isManualVerificationLocked && isIncomeVerified ? 'neutral' : 'primary'}
                onClick={onOpen}
                // Locked disables editing only once proof exists (manual review submitted). With no
                // documents (e.g. the user deleted them after a failure) keep upload enabled so they
                // can provide the required documents.
                disabled={!!isManualVerificationLocked && isIncomeVerified}
              >
                {isIncomeVerified ? `Edit documents` : `Upload documents`}
              </Button>
            )}
            {isIncomeVerified || isDocumentSkipped ? (
              <>
                {isIncomeVerified && !isOpen ? (
                  <Box marginY="small">
                    <Alert tone="positive">{verificationAlertMessage}</Alert>
                  </Box>
                ) : (
                  <Alert tone="caution" icon={InformationCircleIcon}>
                    {label.skipDocumentWarning}
                  </Alert>
                )}
              </>
            ) : (
              <>
                {errors.applicant?.income?.isAdditionalIncome || errors.applicant?.income ? (
                  <Alert tone="critical" icon={InformationCircleIcon}>
                    Income verification documents are required
                  </Alert>
                ) : null}
              </>
            )}
          </Field>
          {incomeType === 'primary' ? renderAdditionalIncomeToggle() : null}
        </>
      );
    }
  };

  useEffect(() => {
    setValue('applicant.financeAmount', financeAmount);
    if (isFieldExists(formFields, 'payment.isCashApplication')) {
      setValue('payment.isCashApplication' as FieldPath<IncomeVerificationType>, isCashApplication);
    }
  }, [financeAmount, isCashApplication]);
  return (
    <>
      <Field label={label.income} description={label.incomeDescription}>
        <Columns gap="medium" collapseBelow="tablet">
          <Box width="full" className={modifiedFieldGapClass}>
            <SelectField
              data={{ testid: 'applicant-income-period-select' }}
              name={`applicant.income.${incomeType}.period`}
              control={control}
              label=""
              options={TIME_FREQUENCY}
              placeholder={PLACEHOLDER}
              fieldProps={{ labelVisibility: 'hidden' }}
              // to revalidate amount when period changes
              onChange={() => {
                if ((applicantIncomeAmount ?? 0) > 0) {
                  //case if user inputs amount before selecting period
                  setShowBanner(true);
                  trigger(`applicant.income.${incomeType}.amount`);
                }
              }}
            />
          </Box>
          <Box width="full" className={modifiedFieldGapClass}>
            <CurrencyInputField
              data={{ testid: 'applicant-income-amount-input' }}
              name={`applicant.income.${incomeType}.amount`}
              control={control}
              label=""
              FieldProps={{ labelVisibility: 'hidden' }}
              onBlur={() => setShowBanner(true)} //only show alert banner after initial input
            />
          </Box>
        </Columns>

        {showBanner && applicantIncomePeriod && (applicantIncomeAmount ?? 0) > 0 && (
          <FinancialAlertBanner
            amount={applicantIncomeAmount ?? 0}
            period={applicantIncomePeriod}
            financialFieldType={FinancialFieldTypes.PRIMARY}
          />
        )}
      </Field>

      {renderVerificationDetail()}
      <IncomeVerificationOptions
        name={`applicant.income.${incomeType}`}
        isOpen={isOpen}
        onToggle={onToggle}
        financialProductId={financialProductId}
        vendorId={quote?.vendor?.publicId || ''}
        incomeType={incomeType}
        onVerificationFailed={handleVerificationFailed}
      />

      <input
        type="hidden"
        {...register(`applicant.income.${incomeType}.documents`, {
          setValueAs: v => (v ? v : []),
        })}
      />
      <input type="hidden" {...register(`applicant.income.${incomeType}.bankStatementToken`)} />
      <input hidden type="checkbox" {...register(`applicant.income.${incomeType}.bankStatementComplete`)} />
    </>
  );
}

export default IncomeFields;
